<script lang="ts">
	import { format } from 'svelte-i18n';

	import { push } from 'svelte-spa-router';

	import { home_data } from 'src/stores/account/user';
	import { active_integrations } from 'src/stores/integrations/alexa';
	import { devices } from 'src/stores/lists/devices';

	import {
		CardGrid,
		Container,
		Label,
		LinkWithIcon,
		SelectCard,
		Text,
	} from '@Shelly/shelly-design-system';

	import { getCuryDevicesNumber } from 'src/helpers/cury_mode_helpers';
	import { getLoqedDevicesNumber } from 'src/helpers/loqed_helpers';
	import { capFirstChar } from 'src/helpers/utilities';

	type IntegrationBadge = {
		icon: string;
		iconColor?: string;
		textClass?: string;
		value: string;
	};

	type IntegrationCard = {
		id: number;
		key: string;
		text: string;
		descriptiveText: string;
		image: string;
		route: string;
		leftBadge: IntegrationBadge;
		secondLeftBadge?: {
			value: string;
			type: 'secondary' | 'warning';
		};
	};

	$: alexaConnected = $active_integrations.includes('alexa');
	$: zendureAssociated = Boolean($home_data?.zendure?.user);
	$: loqedDevices = ($devices, getLoqedDevicesNumber());
	$: curyDevices = ($devices, getCuryDevicesNumber());

	$: integrationsCards = [
		{
			id: 0,
			key: 'alexa',
			text: 'Amazon Alexa',
			descriptiveText: $format('text_integrate_amazon_alexa', {
				default: 'Easily control your devices with voice commands.',
			}),
			image: './images/alexa-logo-square.png',
			route: '/app-to-app-alexa',
			leftBadge: {
				iconColor: alexaConnected ? '--color-success' : '--color-muted',
				icon: `fas fa-${alexaConnected ? 'check' : 'xmark'}`,
				textClass: alexaConnected ? 'text-success' : 'text-color-muted',
				value: capFirstChar(
					alexaConnected
						? $format('connected', { default: 'Connected' })
						: $format('label_not_connected', { default: 'not connected' }),
				),
			},
		},
		{
			id: 1,
			key: 'zendure',
			text: 'Zendure',
			descriptiveText: $format('text_integrate_zendure', {
				default: 'PV consumption monitoring and device management.',
			}),
			image: './images/zendure-logo-square.png',
			route: '/zendure',
			leftBadge: {
				iconColor: zendureAssociated ? '--color-success' : '--color-muted',
				icon: `fas fa-${zendureAssociated ? 'check' : 'xmark'}`,
				textClass: zendureAssociated ? 'text-success' : 'text-color-muted',
				value: capFirstChar(
					zendureAssociated
						? $format('label_associated', { default: 'Associated' })
						: $format('label_not_connected', { default: 'not connected' }),
				),
			},
			secondLeftBadge: {
				value: $format('alpha_title', { default: 'Alpha' }),
				type: 'warning',
			},
		},
		{
			id: 2,
			key: 'loqed',
			text: 'LOQED',
			descriptiveText: $format('text_integrate_loqed_device', {
				default: 'Integrate your LOQED devices seamlessly.',
			}),
			image: './images/loqed-logo-square.png',
			route: '/loqed',
			leftBadge: {
				icon: 'fas fa-microchip',
				iconColor: '--color-muted',
				textClass: 'text-color-muted',
				value: $format('label_devices_count', {
					default: '{count} devices',
					values: { count: loqedDevices },
				}),
			},
			secondLeftBadge: {
				value: $format('alpha_title', { default: 'Alpha' }),
				type: 'warning',
			},
		},
		{
			id: 3,
			key: 'cury',
			text: 'Cury',
			descriptiveText: $format('text_control_cury_devices', {
				default: 'Control your Cury devices with the Cury app.',
			}),
			image: './images/cury-logo.png',
			route: '/app-to-app-cury',
			leftBadge: {
				icon: 'fas fa-microchip',
				iconColor: '--color-muted',
				textClass: 'text-color-muted',
				value: $format('label_devices_count', {
					default: '{count} devices',
					values: { count: curyDevices },
				}),
			},
			secondLeftBadge: {
				value: $format('label_new', { default: 'NEW' }),
				type: 'secondary',
			},
		},
	] as IntegrationCard[];
</script>

<Container type="borderSolid" backgroundColor="color-black" noScroll class="mb-s border-color-base">
	<div class="mb-m">
		<Text element="h3" styleType="body">
			{$format('available_integrations', { default: 'Available Integrations' })}
		</Text>
		<Text styleType="body-small" class="mt-s text-color-muted">
			{capFirstChar(
				$format('text_choose_your_integration', { default: 'Choose your integration' }),
			)}
		</Text>
	</div>

	<CardGrid sm={200} md={240} lg={270} xl={270} noPaddingBottom>
		{#each integrationsCards as card (card.key)}
			<SelectCard
				cardBgColor="color-00"
				class="width-auto"
				type="vertical"
				title={card.text}
				descriptiveText={card.descriptiveText}
				hideSecondLeftSlot={!card.secondLeftBadge}
				customActionMinHeight
				image={card.image}
			>
				<Label
					slot="leftBadge"
					size="s"
					icon={card.leftBadge.icon}
					{...card.leftBadge.iconColor ? { iconColor: card.leftBadge.iconColor } : {}}
					value={card.leftBadge.value}
					textClass={card.leftBadge.textClass}
					noRadiusType="second"
				/>
				<Label
					slot="secondLeftBadge"
					size="xs"
					type={card.secondLeftBadge?.type}
					value={card.secondLeftBadge?.value}
					noRadiusType="third"
				/>
				<LinkWithIcon
					iconInRight
					id={`configure-integration-${card.key}`}
					aria-label={$format('button_configure_integration', {
						default: 'Configure {name}',
						values: { name: card.text },
					})}
					on:click={() => push(`/settings/integrations${card.route}`)}
					slot="customAction"
					size="m"
					icon="fa-light fa-arrow-right"
					href="/"
					text={$format('button_configure', { default: 'Configure' })}
				/>
			</SelectCard>
		{/each}
	</CardGrid>
</Container>
